import { CoreButtonView, CoreTextView, type TCoreButtonViewProps } from '@core/widgets';
import { useBehavior, useButton, useDetectDocumentScroll, useTriggerAction } from '@libs/hooks';
import { AssetsSvgMap, TButtonData, TWidgetProps } from '@libs/types';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { TButtonCircleAnimated, TVisualOption } from '../../types/options';
import { VisualView } from '../Image/VisualView';
import { getCircleBg, getCircleHoverBg, getCircleIconColor, getCirclePressedBg, getTextColor } from './styles/buttonCircleUtils';
import styles from './styles/buttonCircleAnimated.module.css';

const ICON_HIERARCHY_MAP: Record<string, TVisualOption['iconHierarchy']> = {
    primary: 'highlighted',
    highlighted: 'highlighted',
    tertiary: 'secondaryAlt',
    destructive: 'destructive',
};

export const ButtonCircleAnimated = ({ widget }: TWidgetProps<TButtonData, TButtonCircleAnimated>) => {
    const data = widget.data ?? {};
    const options = widget.options ?? {};
    const { isDisabled, isHidden } = useBehavior(widget);
    const { triggerActionsByType, triggerActionsByNameAndType } = useTriggerAction(widget);
    const { handleClick } = useButton(widget, { isDisabled, triggerActionsByType, triggerActionsByNameAndType });
    const { documentScrolled } = useDetectDocumentScroll(widget.uid);

    const iconName = options.iconName;
    const hierarchy = options.hierarchy ?? 'tertiary';

    const [isExpanded, setIsExpanded] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!isHidden) {
            timeoutRef.current = setTimeout(
                () => {
                    setIsExpanded(true);
                    timeoutRef.current = setTimeout(() => setIsExpanded(false), (options.timeToShrink || 30) * 1000);
                },
                (options.timeToExpand || 5) * 1000,
            );
        } else if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [options.timeToShrink, options.timeToExpand, isHidden]);

    const getPosition = () => {
        const dynamicShadow = options.dynamicShadow ?? true;
        if (dynamicShadow && documentScrolled && !isDisabled) {
            return 'floating';
        }
        return options.position;
    };
    const raised = getPosition() === 'floating' && !isDisabled;
    const parentStyleNames = [widget.options?.style ?? ' '].filter(Boolean) as string[];
    const label = data.messages?.label ?? data.label;

    return (
        <CoreButtonView
            widget={widget as TCoreButtonViewProps['widget']}
            text={label}
            isDisabled={isDisabled}
            isHide={isHidden}
            parentStyleNames={parentStyleNames}
            styleName={'buttonCircleAnimated'}
            styleProps={{
                main: getCircleBg(hierarchy, isDisabled, options.buttonColor),
                onMain: getCircleIconColor(hierarchy, isDisabled),
                borderRadius: 20,
                width: isExpanded ? 200 : 40,
                height: 40,
                padding: { top: 0, right: 0, bottom: 0, left: 0 },
                margin: options.margin,
                boxShadow: raised ? 'var(--color-shadow_grey)' : undefined,
                widthTransition: '2s ease',
                cursorDisabled: 'default',
                textAlign: 'start',
                disabled: {
                    main: getCircleBg(hierarchy, isDisabled, options.buttonColor),
                    onMain: getCircleIconColor(hierarchy, isDisabled),
                },
                hovered: {
                    main: getCircleHoverBg(hierarchy),
                    onMain: getCircleIconColor(hierarchy, isDisabled),
                },
                pressed: {
                    main: getCirclePressedBg(hierarchy),
                    onMain: getCircleIconColor(hierarchy, isDisabled),
                },
            }}
            onClick={() => handleClick()}
        >
            {label && (
                <span className={clsx(styles.text, isExpanded && styles.textVisible)}>
                    <CoreTextView
                        as={'span'}
                        text={label}
                        isDisabled={isDisabled}
                        parentStyleNames={parentStyleNames}
                        styleName={'buttonCircleAnimatedText'}
                        styleProps={{
                            main: getTextColor(hierarchy, isDisabled),
                        }}
                    />
                </span>
            )}
            <div className={styles.iconContainer}>
                <VisualView
                    iconName={iconName}
                    defaultIconName={AssetsSvgMap.ic_x}
                    iconHierarchy={ICON_HIERARCHY_MAP[hierarchy]}
                    iconVariant="simple"
                    isDisabled={isDisabled}
                    parentStyleNames={[...parentStyleNames, 'buttonCircleAnimatedIcon']}
                />
            </div>
        </CoreButtonView>
    );
};
